import { message } from "antd";
import { _TOKEN_ } from "const";
import store from "redux/store";
import { detect } from "detect-browser";
import includes from "lodash/includes";
import groupBy from "lodash/groupBy";
import toLower from "lodash/toLower";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import compact from "lodash/compact";
import concat from "lodash/concat";

export function isMobileDevice() {
  let isMobile = false;
  if (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
      navigator.userAgent
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
      navigator.userAgent.substr(0, 4)
    )
  ) {
    isMobile = true;
  }
  return isMobile;
}

export function applyPaging(dataSource, paginable) {
  const { current, pageSize } = paginable;
  var offset = (current - 1) * pageSize;
  return dataSource.slice(offset, offset + pageSize);
}

export function toPagingServer(paginable, next = false) {
  return {
    pageIndex: (+paginable?.current ?? 0) + (next ? 1 : 0),
    rowPerPage: paginable?.pageSize,
  };
}

export const clazz = (...clazzes) => {
  return clazzes
    .reduce((r, o) => {
      return typeof o === "string" || typeof o === "undefined"
        ? `${r} ${o || ""}`
        : o.cond
          ? `${r} ${o.clz}`
          : o.else
            ? `${r} ${o.else}`
            : r;
    }, "")
    .trim();
};

export function escapeRegExp(text) {
  if (!text) return;
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

export const jsonPretty = (text = "") => {
  try {
    const jsonObj = JSON.parse(text.trim());
    text = JSON.stringify(jsonObj, null, "\t");
  } catch (error) { }
  return text;
};

export const copyTextToClipBoard = (text = "", config = {}) => {
  text = text.trim();
  if (config.autoFormatJson) {
    text = jsonPretty(text);
  }

  const input = document.createElement("textarea");
  input.innerHTML = text;
  document.body.appendChild(input);
  input.select();
  const result = document.execCommand("copy");
  document.body.removeChild(input);

  return result;
};

export const scroll = (
  element,
  options = {
    // top, left, behavior
  }
) => {
  try {
    if (element) {
      const _options = {
        behavior: "smooth",
        ...options,
      };
      element.scroll(_options);
    }
  } catch (error) {
    console.error("error:", error?.message);
  }
};

const getHTMLElement = (selector) => {
  return typeof selector === "string" ? document.querySelector(selector) : selector;
};

export const scrollToRight = (selector, offset = 0) => {
  const elem = document.querySelector(selector);
  scroll(elem, { left: elem.offsetWidth + offset });
};

export const scrollToTop = (selector, offset = 0) => {
  const elem = getHTMLElement(selector) ?? document.documentElement ?? document.body;
  scroll(elem, { top: 0 + offset });
};

export const scrollToViewAdjustHeader = (elementSelector, offsetElementSelector, offset = 0) => {
  const element = getHTMLElement(elementSelector);
  if (element) {
    const header = getHTMLElement(offsetElementSelector);
    const headerOffset = header?.getBoundingClientRect().height ?? 0;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition - headerOffset - offset;

    window.scrollBy({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

export const scrollIntoView = (selector, opt = {}) => {
  const elem = typeof selector === "string" ? document.querySelector(selector) : selector;
  if (elem) {
    elem.scrollIntoView({ behavior: "smooth", ...opt });
  }
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return phone;
  return phone.replace(/(\d{3})(\d{3})(\d{4})/, (_, f, s, t) => `${f}-${s}-${t}`);
};

export const removeFormatPhoneNumber = (phone) => {
  if (!phone) return phone;
  return phone.trim().replace(/-/g, "");
};

export const isElementInViewport = (element, offset = {}) => {
  if (!element) return true;

  const { bottom: offsetBottom = 0 } = offset;

  var top = element.offsetTop;
  var left = element.offsetLeft;
  var width = element.offsetWidth;
  var height = element.offsetHeight;

  while (element.offsetParent) {
    element = element.offsetParent;
    top += element.offsetTop;
    left += element.offsetLeft;
  }

  // console.log({
  //     lay: { top, left, width, height },
  //     win: { y: window.pageYOffset, x: window.pageXOffset, inHeight: window.innerHeight, inWidth: window.innerWidth },
  //     topCheck: top < (window.pageYOffset + window.innerHeight),
  //     leftCheck: left < (window.pageXOffset + window.innerWidth),
  //     bottomCheck: (top + height) > (window.pageYOffset + offsetBottom),
  //     rightCheck: (left + width) > window.pageXOffset,
  // })

  return (
    top < window.pageYOffset + window.innerHeight &&
    left < window.pageXOffset + window.innerWidth &&
    top + height > window.pageYOffset + offsetBottom &&
    left + width > window.pageXOffset
  );
};

export const throttleSingleEvent = (func, interval) => {
  let timeout;
  return (...args) => {
    const context = this;
    const openIncomingCall = () => {
      timeout = false;
    };
    if (!timeout) {
      func.apply(context, args);
      timeout = true;
      setTimeout(openIncomingCall, interval);
    }
  };
};

export const loadImage = (imgElem, image) => {
  if (!image || !imgElem) return;
  const reader = new FileReader();
  reader.onload = function (e) {
    imgElem.src = e.target.result;
  };
  reader.readAsDataURL(image);
};

export const getChangeValue = (x, config = {}) => {
  const { convertNumToStr, convertStrToNum, boolToTextConvertor } = config;

  // retrieve data
  let result;
  switch (typeof x) {
    case "object":
      if (x) {
        if (Array.isArray(x)) {
          result = x;
        } else {
          const target = x.target;
          result = target.value !== undefined ? target.value : target.checked;
        }
      } else {
        result = x;
      }
      break;
    case "string":
    case "number":
    default:
      result = x;
  }

  // convert zone
  switch (typeof result) {
    case "string":
      if (convertStrToNum && !Number.isNaN(+result)) result = +result;
      break;
    case "number":
      if (convertNumToStr) result = "" + result;
      break;
    case "boolean":
      if (boolToTextConvertor) result = boolToTextConvertor(result);
      break;
    default:
      break;
  }

  return result;
};

export const toOptions = (list, label, value) => {
  if (!list) return list;
  return list.map((data) => {
    if (typeof data === "object") {
      data.label = typeof label === "function" ? label(data) : data[label];
      data.value = typeof value === "function" ? value(value) : data[value];
    } else {
      data = { label: data, value: data };
    }
    return data;
  });
};

export function addHiddenField(object, field, value) {
  if (!object[field]) {
    Object.defineProperty(object, field, {
      enumerable: false,
      writable: true,
      configurable: true,
      value: value,
    });
  } else {
    object[field] = value;
  }
}

export const toUpperCase = (object, fields = []) => {
  fields.forEach((field) => {
    const original = object[field];
    if (original && typeof original === "string") {
      object[field] = original.toUpperCase();
    }
  });
  return object;
};

export const isFunction = (func) => typeof func === "function";

const windowStorage = {
  getItem: (key) => window[key],
  setItem: (key, value) => (window[key] = value),
  removeItem: (key) => delete window[key],
};
const getStorage = () => {
  // return process.env.NODE_ENV !== 'production' ? sessionStorage : windowStorage
  return windowStorage;
};
export const getToken = () => getStorage().getItem(_TOKEN_);
export const setToken = (token) => getStorage().setItem(_TOKEN_, token);
export const removeToken = () => getStorage().removeItem(_TOKEN_);

export function convertArrayToObject(array = [], key) {
  return array.reduce((acc, obj) => {
    acc[obj[key]] = obj;
    return acc;
  }, {});
}

export function convertObjectToArray(object = {}) {
  return Object.keys(object).map((field) => object[field]);
}

export const getCurrentLocale = (store) => store.common?.locale?.locale ?? "th"; // default thai [ ?? 'th' ]
export const isThai = (store) => (getCurrentLocale(store) || "").toLowerCase() === "th";
export const getMessageByLocale = (store, th, en) => (isThai(store) ? th : en);

export const loopAsync = async (list = [], func, method = "forEach") => {
  if (!list || list.length === 0) {
    return list;
  }

  let endResolve, rejectLoop;
  const endPromise = new Promise((r, c) => {
    endResolve = r;
    rejectLoop = c;
  });
  const result = [];

  const promiseList = list
    .map((obj, index) => {
      let resolve;
      const promise = index === 0 ? Promise.resolve() : new Promise((r) => (resolve = r));
      return [obj, promise, resolve];
    })
    .map((stuff, i, array) => {
      const nextObj = array[i + 1] ?? [];
      const resolverNext = nextObj[2] ?? endResolve;
      const [obj, promise] = stuff;
      return [obj, promise, resolverNext];
    });

  try {
    promiseList[method](async (po, i, array) => {
      const [o, promise, next] = po;
      const payload = await promise;
      result.push(payload);
      func(o, next, rejectLoop, { index: i, payload, array, dispatch: store.dispatch });
    });

    const payload = await endPromise;
    result.push(payload);
    return result.slice(1); // delete first dummy result
  } catch (error) {
    return Promise.reject(error);
  }
};

export function stringFormat(text, params) {
  let currentIndex = 0;
  return (
    text?.replace(/\{(\d+)?\}/g, (_, specificIndex) => {
      if (specificIndex) {
        currentIndex = +specificIndex + 1;
        return params?.[+specificIndex] ?? "";
      } else {
        return params?.[currentIndex++] ?? "";
      }
    }) ?? text
  );
}

export function isStringTemplate(template) {
  return template?.match(/@\{(\w+)\}/)?.length > 0;
}

export function stringTemplate(template, data) {
  return template?.replace(/@\{(\w+)\}/g, (_, field) => data?.[field] ?? "");
}

export function formatAccountDisplay(accountNo) {
  if (!accountNo) return accountNo;
  return accountNo.padStart(10, "0").replace(/(\d{3})(\d{1})(\d{5})(\d{1})/, "$1-$2-$3-$4");
}

export const resolve = (data, ...fields) => {
  if (!data) return;
  if (data && fields && !fields[0]) return data;

  return fields
    .reduce(
      (results, field) => (field && field.indexOf(".") > -1 ? [...results, ...field.split(".")] : [...results, field]),
      []
    )
    .reduce((result, field) => (result ? result[field] : undefined), data);
};

export const mergeObj = (...objects) => {
  return objects.reduce((result, obj) => {
    if (typeof obj === "object") {
      // array or object
      if (!Array.isArray(obj)) {
        return { ...result, ...obj };
      } else {
        return mergeObj(result, ...obj);
      }
    } else {
      console.warn("Merge ignore: can not merge primitive value:", obj);
      return result;
    }
  }, {});
};

export const optionByLocale = (list, isLocaleTH, valueField, fieldTH, fieldEN, additionalField) => {
  const labelField = isLocaleTH ? fieldTH : fieldEN;
  return list.map((m) => {
    const option = {
      label: isStringTemplate(labelField) ? stringTemplate(labelField, m) : m[labelField],
      value: isStringTemplate(valueField) ? stringTemplate(valueField, m) : m[valueField],
    };
    if (additionalField) {
      for (const field in additionalField) {
        option[field] = additionalField[field](isLocaleTH, m);
      }
    }
    return option;
  });
};

export function concatAll(object, sep = " ", fields) {
  return fields.reduce((acc, field) => (object[field] ? `${acc}${sep}${object[field]}` : acc), "");
}

export function concatAddress(object, sep = " ") {
  const addressFields = [
    "mailingAddress",
    "mailingSubDistrictTxt",
    "mailingDistrictTxt",
    "mailingProvinceTxt",
    "mailingZipCode",
  ];
  return concatAll(object, " ", addressFields);
}

export function mockResponse(result, field = "result") {
  return Promise.resolve({ data: { [field]: cloneDeep(result) } });
}

export const isNotNullish = (v) => v !== null && v !== undefined;
export const isNullish = (v) => v === null || v === undefined;
export const isNotEmpty = (v) => v !== null && v !== undefined && v !== "" && (Array.isArray(v) ? v.length > 0 : true);
export const isEmpty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

export const disableButtonInvalidForm = (form) => {
  return form.getFieldsError().filter(({ errors }) => errors.length).length > 0;
  // return !form.isFieldsTouched(true) || form.getFieldsError().filter(({ errors }) => errors.length).length > 0;
};

export const isBrowserIn = (browsers) => {
  if (isEmpty(browsers)) return false;
  if (typeof browsers === "string") browsers = [browsers];
  const browser = detect();
  return browsers.some((b) => b.toLowerCase() === browser.name);
};

export const loseFocus = () => {
  setTimeout(() => document.querySelector(":focus")?.blur(), 0);
};

export const isTrue = (value) => typeof value === "boolean" && value === true;
export const isFalse = (value) => typeof value === "boolean" && value === false;

export const paddingZeroAccountNo = (accountNo) => accountNo?.padStart(10, "0");

export const filterFields = (data, fields) => {
  return fields.reduce((result, field) => {
    result[field] = data[field];
    return result;
  }, {});
};

export const shouldUpdate =
  (...fields) =>
    (prev, curr) => {
      if (!fields || fields.length === 0) return true;
      return fields.some((field) => prev[field] !== curr[field]);
    };

export const getSettledPromiseValue = (response, options = {}) => {
  return response.status === "fulfilled" ? [response.value] : [options.fallback, response.reason]; // rejected
};

export const renderProps = (render) => (isFunction(render) ? render() : render);

// export const removeAllSpace = (text) => text?.replaceAll(' ', '')
export const removeAllSpace = (text) => text?.replace(/\s+/g, "");

export const filterOption = (labelField) => (input, option) => {
  let labelFilters = option[labelField];
  if (!Array.isArray(labelFilters)) labelFilters = [labelFilters];
  return labelFilters.some((labelFilter) => {
    const label = removeAllSpace(toLower(labelFilter));
    const search = removeAllSpace(toLower(input));
    return includes(label, search);
  });
};

export const conditionExp = (...conditions) => {
  return conditions.find(([exp]) => isNullish(exp) || (isFunction(exp) ? exp() : exp))?.[1];
};

export const blockEvent = (evt) => {
  evt.stopPropagation();
  evt.preventDefault();
};

export const group = (list, groupField, options) => {
  const { listName = "list", map } = options ?? {};
  return Object.entries(groupBy(list, groupField)).map(([group, groupList]) => ({
    [groupField]: group,
    [listName]: map ? map(groupList) : groupList,
  }));
};

export const groupFilter = (_groups, pathList, filter) => {
  const groups = cloneDeep(_groups);
  groups.forEach((group) => set(group, pathList, (get(group, pathList) ?? []).filter(filter)));
  return groups.filter((group) => get(group, pathList).length > 0);
};

export function toUrlSearchParam(params) {
  if (!params) return "";
  return Object.entries(params).reduce((acc, [name, value], index) => {
    const concat = index === 0 ? "?" : "&";
    return isNotNullish(value) ? `${acc}${concat}${name}=${value}` : acc;
  }, "");
}

export function countChar(text, char) {
  const regex = typeof char === "string" ? new RegExp(char, "g") : char;
  return text?.match(regex)?.length ?? 0;
}

export function concatCompact(...array) {
  // console.log(compact(concat(...array)), array)
  return compact(concat(...array));
}

export function counter(start) {
  start = start || 0;

  function* counter() {
    let count = start;
    while (true) {
      yield ++count;
    }
  }

  const count = counter();
  let current = start;

  return {
    value: () => current,
    next: () => {
      current = count.next().value;
      return current;
    },
  };
}

export const removeNullishFields = (obj) => {
  if (obj) {
    for (const key in obj) {
      if (obj[key] === undefined || obj[key] === null) {
        delete obj[key];
      }
    }
  }

  return obj;
};

export const DocumentUtil = {
  enableScroll: () => {
    document.body.classList.remove("overflow-y-hidden");
  },
  disableScroll: () => {
    document.body.classList.add("overflow-y-hidden");
  },
};

export const getPromise = () => {
  let $resolve, $reject;
  const promise = new Promise((resolve, reject) => {
    $resolve = resolve;
    $reject = reject;
  });
  return {
    promise,
    resolve: $resolve,
    reject: $reject,
  };
};
